import { getContract } from 'viem';

import { proxyABI } from '../../utils/abis/proxyABI';
import { claimABI } from "../../utils/abis/claimABI";
import { tokenlockABI as tokenlockABIV1 } from '../../utils/abis/stake/v1/tokenlockabi';
import { tokenlockABI as tokenlockABIV2 } from '../../utils/abis/stake/v2/tokenlockabi';
import { stakeTokenABI as partnerStakeABI } from '../../utils/abis/stake/partner/stakeTokenAbi';
import { collector } from "../../utils/abis/pledgeContributorABI";
import { multicallABI } from "../../utils/abis/multicallABI";
import { Contracts } from '../../config/contracts';
import { getBalanceNumber, getDecimalAmount } from '../../utils/web3/formatBalance';

import { config } from './wagmi';
import { appConfig } from '../../config';
import { getAccount, getGasPrice, getPublicClient, getWalletClient, signMessage, simulateContract, waitForTransactionReceipt, writeContract } from '@wagmi/core';
import { erc20ABI } from '../../utils/abis/erc20ABI';

interface GasRequest {
  address: `0x${string}`;
  abi: any;
  functionName: string;
  account?: `0x${string}` | undefined;
  args: readonly any[];
}

interface GasFeeData {
  gasPrice: bigint;
  // Add other relevant fields if any
}

export class LowGasError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LowGasError';
  }
}

class Web3Helper {

  private lockContractSupportedVersions = appConfig.lockContractSupportedVersions.split(',').map(Number);

  private latestLockContractVersion = parseInt(appConfig.latestLockContractVersion.toString(), 10);


  async waitOrTimeoutForTransactionReceipt (hash: `0x${string}`, timeout: number) {
    const receipt = await waitForTransactionReceipt(config, { hash, timeout });
    return receipt;
  }


  async getGasFees(): Promise<GasFeeData> {
    try {
      const feeData = await getGasPrice(config);
      if (!feeData) throw new Error('Failed to fetch gas fees');
      return { gasPrice: feeData};
    } catch (error) {
      console.error('Error fetching gas fees:', error);
      throw error;
    }
  }
  
  async estimateGas(chainId: number, request: GasRequest): Promise<bigint> {
    try {
      const publicClient = getPublicClient(config, { chainId });
      if (!publicClient) throw new Error('Failed to get public client');
      const gas = await publicClient.estimateContractGas({
        address: request.address,
        abi: request.abi,
        functionName: request.functionName,
        account: request.account,
        args: request.args,
      });
      if (!gas) throw new Error('Failed to estimate gas');
      return gas;
    } catch (error) {
      console.error('Error estimating gas:', error);
      throw error;
    }
  }

  getLockContractABI (scVersion: number) {
    if (!this.lockContractSupportedVersions.includes(scVersion)) {
      throw new Error(`Unsupported lock contract version: ${scVersion}`);
    }
    
    if (scVersion === 1) {
      return tokenlockABIV1;
    } else if (scVersion === 2) {
      return tokenlockABIV2;
    } else {
      throw new Error(`Unsupported lock contract version: ${scVersion}`);
    }
  }
  
  async checkGasForTransaction(address: `0x${string}`,chainId: number, request: GasRequest): Promise<void> {
    try {
      const gasLimit = await this.estimateGas(chainId, request);
      const feeData = await this.getGasFees();
      const gasPrice = feeData.gasPrice;
      const gasCost = gasLimit * gasPrice;
      // return gasCost.toString();
      const publicClient = getPublicClient(config, { chainId });
      const userBalance = await publicClient?.getBalance({ address });

      if (userBalance === null || userBalance === undefined) throw new Error('Unable to fetch user balance');

      if (BigInt(userBalance) < BigInt(gasCost.toString())) {
        throw new LowGasError('Insufficient balance to pay for gas');
      }
    } catch (error) {
      console.error('Error checking gas for transaction:', error);
      throw error;
    }
  }

  async signMessage (message: string): Promise<`0x${string}` | null> {
    return new Promise(async (resolve, reject) => {
      signMessage(config, { message })
      .then((signature) => {
        resolve(signature);
      }).catch((error) => {
        reject(error);
      })
    });
  }

  getERC20Contract (chainId: number, tokenAddress: `0x${string}`) {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) throw new Error('Unable to create a public client');
    const erc20Contract = getContract({
      address: tokenAddress,
      abi: erc20ABI,
      client: publicClient,
    });
    return erc20Contract;
  }

  async approveTokenSpend (chainId: number, tokenAddress: `0x${string}`, tokenAmount: number, spender: `0x${string}`) {
    const { address } = getAccount(config);

    if (!address) {
      throw new Error('User address not found');
    }

    const erc20Contract =  this.getERC20Contract(chainId, tokenAddress);
    const decimals = await erc20Contract.read.decimals();
    const tokenAmountInWei = getDecimalAmount(tokenAmount.toString(), decimals).toString();
    const { request } = await simulateContract(config, {
      abi: erc20ABI,
      address: tokenAddress,
      functionName: 'approve',
      args: [spender, BigInt(tokenAmountInWei)],
      account: address,
      chainId,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };

    await this.checkGasForTransaction(address, chainId, gasRequest);

    const hash = await writeContract(config, request);
    const receipt = waitForTransactionReceipt(config, {hash});
    return receipt;
  }

  async getLockFactoryContract (chainId: number, scVersion: number) {
    const publicClient = getPublicClient(config, { chainId });
    const walletClient = await getWalletClient(config, { chainId });
    if (!publicClient || !walletClient) throw new Error('Unable to create a client');
    const lockAddresses = Contracts.lockFactory[scVersion.toString()] as Record<string, `0x${string}`>;
    const contractAddress = lockAddresses[chainId.toString()];
    const tokenlockABI = this.getLockContractABI(scVersion);
    const lockContract = getContract({
      address: contractAddress,
      abi: tokenlockABI,
      client: { public: publicClient, wallet: walletClient }
    })
    return lockContract;
  }

  async createGainsLockedWallet (args: readonly [bigint, bigint, string]) {
    const { address: account, chain } = getAccount(config);
    if (!account) throw new Error('No account information discovered');
    if (!chain) throw new Error('No chain information discovered');
    const lockAddresses = Contracts.lockFactory[this.latestLockContractVersion] as Record<string, `0x${string}`>;
    const contractAddress = lockAddresses[chain.id.toString()];
    const tokenlockABI = this.getLockContractABI(this.latestLockContractVersion);
    const { request } = await simulateContract(config, {
      address: contractAddress,
      abi: tokenlockABI,
      functionName: 'createGainsLockedWallet',
      args,
      chainId: chain.id,
      account,
    });
    
    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };

    await this.checkGasForTransaction(account, chain.id, gasRequest);  
    const hash = await writeContract(config, request);
    const receipt = waitForTransactionReceipt(config, { hash });
    return receipt;
  }

  async relockWallet (args: readonly [string, string, bigint], account: `0x${string}`, scVersion: number) {
    if (scVersion !== this.latestLockContractVersion) throw new Error(`Unsupported lock contract version: ${scVersion} for relocking. Please withdraw and stake again.`);
    const { chain } = await getAccount(config);
    if (!chain) throw new Error(`Unable to fetch chain information`);
    const lockAddresses = Contracts.lockFactory[this.latestLockContractVersion] as Record<string, `0x${string}`>;
    const contractAddress = lockAddresses[chain.id.toString()];
    const tokenlockABI = this.getLockContractABI(this.latestLockContractVersion);
    const { request } = await simulateContract(config, {
      address: contractAddress,
      abi: tokenlockABI,
      functionName: 'relockGainsBySubscription',
      args,
      chainId: chain.id,
      account,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };
    
    await this.checkGasForTransaction(account, chain.id, gasRequest);
    const hash = await writeContract(config, request);
    const receipt = await waitForTransactionReceipt(config, { hash });
    return receipt;
  }

  async withdrawGainsFromLockedWallet (args: readonly [string], account: `0x${string}`, scVersion: number) {
    try {
      const { chain } = await getAccount(config);
      if (!chain) throw new Error(`Unable to fetch chain information`);
      const lockAddresses = Contracts.lockFactory[scVersion.toString()] as Record<string, `0x${string}`>;
      const contractAddress = lockAddresses[chain.id.toString()];
      const tokenlockABI = this.getLockContractABI(scVersion);
      const { request } = await simulateContract(config, {
        address: contractAddress,
        abi: tokenlockABI,
        functionName: 'withdrawGains',
        args,
        chainId: chain.id,
        account,
      });
  
      const gasRequest: GasRequest = {
        address: request.address,
        abi: request.abi,
        functionName: request.functionName,
        account: request.account.address,
        args: request.args,
      };
  
      await this.checkGasForTransaction(account, chain.id, gasRequest);
  
      const hash = await writeContract(config, request);
      const receipt = await waitForTransactionReceipt(config, { hash });
      return receipt;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  getPledgeContract (address: `0x${string}`, chainId: number) {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) throw new Error('Unable to create a public client');
    const pledgeContract = getContract({
      address,
      abi: collector,
      client: publicClient,
    });
    return pledgeContract;
  }

  async contributeWithPledge (args: readonly [`0x${string}`, `0x${string}`, bigint, bigint, readonly `0x${string}`[]], account: `0x${string}`) {
    const { chain } = await getAccount(config);
    if (!chain) throw new Error(`Unable to fetch chain information`);
    const { request } = await simulateContract(config, {
      address: Contracts.proxy[chain.id] as `0x${string}`,
      abi: proxyABI,
      functionName: 'contributeWithPledge',
      args,
      chainId: chain.id,
      account,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };

    await this.checkGasForTransaction(account, chain.id, gasRequest);

    const hash = await writeContract(config, request);
    const receipt = await waitForTransactionReceipt(config, { hash });
    return receipt;
  }

  async claimToken (claimTokenAddress: `0x${string}`, args: readonly [readonly `0x${string}`[], bigint] | (readonly [readonly `0x${string}`[], bigint] & readonly [readonly `0x${string}`[], bigint]), account: `0x${string}`) {
    const { chain } = getAccount(config);
    if (!chain) throw new Error(`Unable to fetch chain information`);
    const { request } = await simulateContract(config, {
      address: claimTokenAddress,
      abi: claimABI,
      functionName: 'claim',
      args,
      chainId: chain.id,
      account,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };

    await this.checkGasForTransaction(account, chain.id, gasRequest);

    const hash = await writeContract(config, request);
    const receipt = await waitForTransactionReceipt(config, { hash });
    return receipt;
  }

  getClaimContract (chainId: number, claimTokenAddress: `0x${string}`) {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) throw new Error('Unable to create a public client');
    const pledgeContract = getContract({
      address: claimTokenAddress,
      abi: claimABI,
      client: publicClient,
    });
    return pledgeContract;
  }

  getMulticallContract = (
    chainId: number,
  ) => {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) throw new Error('Unable to create a public client');
    const multicallContract = getContract({
      address: Contracts.multicall[chainId] as `0x${string}`,
      abi: multicallABI,
      client: publicClient
    })
    return multicallContract;
  }

  getPartnerStakeContract (chainId: number, stakeContractAddress: `0x${string}`) {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) throw new Error('Unable to create a public client');
    const partnerStakeContract = getContract({
      address: stakeContractAddress,
      abi: partnerStakeABI,
      client: publicClient
    });
    return partnerStakeContract;
  }

  getPartnerStakeTotalStaked = async (chainId: number, stakeContractAddress: `0x${string}`, tokenAddress: `0x${string}`) => {
    const contract = this.getPartnerStakeContract(chainId, stakeContractAddress);
    const totalStaked = await contract.read.totalStaked();
    const erc20Contract =  this.getERC20Contract(chainId, tokenAddress);
    const decimals = await erc20Contract.read.decimals();
    const amount = getBalanceNumber(totalStaked, decimals);
    return amount;
  }

  getUserStake = async (chainId: number, stakeContractAddress: `0x${string}`, tokenAddress: `0x${string}`, userAddress: `0x${string}`) => {
    const contract = this.getPartnerStakeContract(chainId, stakeContractAddress);
    const [amount, startTime, expired] = await contract.read.checkUserStake([userAddress])
    const erc20Contract =  this.getERC20Contract(chainId, tokenAddress);
    const decimals = await erc20Contract.read.decimals();
    let amountStaked = getBalanceNumber(amount, decimals);
    return {
      amountStaked,
      startTime,
      expired
    }
  }

  getUserWithdrawalDetails = async (chainId: number, stakeContractAddress: `0x${string}`, tokenAddress: `0x${string}`, userAddress: `0x${string}`) => {
    const contract = this.getPartnerStakeContract(chainId, stakeContractAddress);
    const [amount, reward] = await contract.read.checkWithdrawalAmount([userAddress])
    const erc20Contract =  this.getERC20Contract(chainId, tokenAddress);
    const decimals = await erc20Contract.read.decimals();
    let amountToWithdraw = getBalanceNumber(amount, decimals);
    let rewardAmount = getBalanceNumber(reward, decimals);
    return {
      amountToWithdraw,
      rewardAmount
    }
  }

  stakeToken = async (args: readonly [bigint], contractAddress: `0x${string}`) => {
    const { address: account, chain } = getAccount(config);
    if (!account) throw new Error('No account information discovered');
    if (!chain) throw new Error('No chain information discovered');
    const { request } = await simulateContract(config, {
      address: contractAddress,
      abi: partnerStakeABI,
      functionName: 'stake',
      args,
      chainId: chain.id,
      account,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args: request.args,
    };

    await this.checkGasForTransaction(account, chain.id, gasRequest);  
    const hash = await writeContract(config, request);
    const receipt = waitForTransactionReceipt(config, { hash });
    return receipt;
  }

  withdrawPartnerStake = async (contractAddress: `0x${string}`) => {
    const { address: account, chain } = getAccount(config);
    if (!account) throw new Error('No account information discovered');
    if (!chain) throw new Error('No chain information discovered');
    let args: readonly any[] = [];
    const { request } = await simulateContract(config, {
      address: contractAddress,
      abi: partnerStakeABI,
      functionName: 'withdraw',
      chainId: chain.id,
      account,
    });

    const gasRequest: GasRequest = {
      address: request.address,
      abi: request.abi,
      functionName: request.functionName,
      account: request.account.address,
      args
    };

    await this.checkGasForTransaction(account, chain.id, gasRequest);  
    const hash = await writeContract(config, request);
    const receipt = waitForTransactionReceipt(config, { hash });
    return receipt;
  }
}
const web3Helper = new Web3Helper();

export default web3Helper;
